import { action, computed, makeObservable } from 'mobx';

import {
  ISlaPolicy,
  SlaPolicyParamsServer,
  validateSlaLimitMinutes
} from 'shared/entities/slaPolicy';
import { validatePositiveIntegerString } from 'shared/entities/validator';
import { FieldModel } from 'shared/models/form';

/**
 * Черновик блока «SLA»: пользователь правит все три поля и сохраняет их одним
 * запросом, поэтому touched/валидность считаются по форме целиком.
 */
export default class SlaSettingsFormModel {
  readonly enabled: FieldModel<boolean>;
  readonly firstResponseMinutes: FieldModel<string>;
  readonly responseMinutes: FieldModel<string>;

  constructor(policy: ISlaPolicy) {
    this.enabled = new FieldModel(policy.enabled);
    this.firstResponseMinutes = new FieldModel(
      String(policy.firstResponseMinutes),
      [validateSlaLimitMinutes]
    );
    this.responseMinutes = new FieldModel(String(policy.responseMinutes), [
      validateSlaLimitMinutes
    ]);

    makeObservable(this, {
      touched: computed,
      isValid: computed,
      canSave: computed,

      changeEnabled: action.bound,
      changeFirstResponseMinutes: action.bound,
      changeResponseMinutes: action.bound,
      validate: action.bound
    });
  }

  get touched(): boolean {
    return (
      this.enabled.touched ||
      this.firstResponseMinutes.touched ||
      this.responseMinutes.touched
    );
  }

  /**
   * Бэкенд принимает лимиты одним payload независимо от enabled, поэтому
   * выключенный SLA не отменяет проверку значений.
   */
  get isValid(): boolean {
    return !this.firstResponseMinutes.isError && !this.responseMinutes.isError;
  }

  get canSave(): boolean {
    return this.touched && this.isValid;
  }

  changeEnabled(value: boolean): void {
    this.enabled.changeValue(value);

    // Невалидный черновик нельзя отправить на backend после выключения SLA:
    // поля блокируются, поэтому возвращаем их к последнему сохранённому значению.
    if (!value && !this.isValid) {
      this.firstResponseMinutes.reset();
      this.responseMinutes.reset();
      return;
    }

    this.validate();
  }

  changeFirstResponseMinutes(value: string): void {
    this.changeLimit(this.firstResponseMinutes, value);
  }

  changeResponseMinutes(value: string): void {
    this.changeLimit(this.responseMinutes, value);
  }

  validate(): boolean {
    this.firstResponseMinutes.validate();
    this.responseMinutes.validate();

    return this.isValid;
  }

  toJSON(): SlaPolicyParamsServer {
    return {
      enabled: this.enabled.value,
      first_response_minutes: Number(this.firstResponseMinutes.value),
      response_minutes: Number(this.responseMinutes.value)
    };
  }

  private changeLimit(field: FieldModel<string>, value: string): void {
    if (value !== '' && validatePositiveIntegerString(value)) {
      return;
    }

    field.changeValue(value);
    field.validate();
  }
}
